/**
 * Portal Auth Components - Login, Register, Password Reset forms
 */
import React, { useState, useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PhoneInput } from '@/components/ui/phone-input';
import { toast } from 'sonner';
import { ChevronLeft, LogIn, UserPlus, Key, Mail, Check, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { useCustomerAuth, usePortalSettings, API } from './PortalContext';
import { portalAuthHeaderValue } from '@/utils/tokenUtils';
import StrongFitLogo from '@/components/StrongFitLogo';

// European Country Codes
const EUROPEAN_COUNTRY_CODES = [
  { code: '+41', country: 'CH', flag: '🇨🇭', name: 'Schweiz' },
  { code: '+49', country: 'DE', flag: '🇩🇪', name: 'Deutschland' },
  { code: '+43', country: 'AT', flag: '🇦🇹', name: 'Österreich' },
  { code: '+33', country: 'FR', flag: '🇫🇷', name: 'Frankreich' },
  { code: '+39', country: 'IT', flag: '🇮🇹', name: 'Italien' },
  { code: '+44', country: 'GB', flag: '🇬🇧', name: 'Grossbritannien' },
  { code: '+34', country: 'ES', flag: '🇪🇸', name: 'Spanien' },
  { code: '+351', country: 'PT', flag: '🇵🇹', name: 'Portugal' },
  { code: '+31', country: 'NL', flag: '🇳🇱', name: 'Niederlande' },
  { code: '+32', country: 'BE', flag: '🇧🇪', name: 'Belgien' },
  { code: '+352', country: 'LU', flag: '🇱🇺', name: 'Luxemburg' },
  { code: '+423', country: 'LI', flag: '🇱🇮', name: 'Liechtenstein' },
  { code: '+45', country: 'DK', flag: '🇩🇰', name: 'Dänemark' },
  { code: '+46', country: 'SE', flag: '🇸🇪', name: 'Schweden' },
  { code: '+47', country: 'NO', flag: '🇳🇴', name: 'Norwegen' },
  { code: '+358', country: 'FI', flag: '🇫🇮', name: 'Finnland' },
  { code: '+48', country: 'PL', flag: '🇵🇱', name: 'Polen' },
  { code: '+420', country: 'CZ', flag: '🇨🇿', name: 'Tschechien' },
  { code: '+421', country: 'SK', flag: '🇸🇰', name: 'Slowakei' },
  { code: '+36', country: 'HU', flag: '🇭🇺', name: 'Ungarn' },
  { code: '+30', country: 'GR', flag: '🇬🇷', name: 'Griechenland' },
  { code: '+353', country: 'IE', flag: '🇮🇪', name: 'Irland' },
  { code: '+386', country: 'SI', flag: '🇸🇮', name: 'Slowenien' },
  { code: '+385', country: 'HR', flag: '🇭🇷', name: 'Kroatien' },
  { code: '+40', country: 'RO', flag: '🇷🇴', name: 'Rumänien' },
  { code: '+359', country: 'BG', flag: '🇧🇬', name: 'Bulgarien' },
];

// Google Icon SVG Component
const GoogleIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

// Dunkel-/Hell-Modus aus der tatsächlichen Theme-Hintergrundfarbe ableiten,
// damit Portale mit hellem Hintergrund die helle Variante der Formulare nutzen.
const themeIsDark = (dt) => {
  const bg = dt && dt.bg;
  if (!bg) return true;
  let h = String(bg).replace('#', '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (h.length !== 6) return true;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 < 0.5;
};


// =============================================
// LOGIN FORM
// =============================================

export const LoginForm = ({ onBack, onForgotPassword, onRegister, darkTheme }) => {
  const { t } = useTranslation();
  const { handleLogin } = useCustomerAuth();
  const settings = usePortalSettings();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleConfig, setGoogleConfig] = useState(null);
  // Customer-Portal Login (Feb 2026) — passwortlos via E-Mail-Code.
  // Drei Modi:
  //  - "otp_request" (Default): E-Mail eingeben → Code per Mail + WhatsApp
  //  - "otp_verify": Code aus E-Mail/WhatsApp eingeben
  //  - "password" (optional, Admin-Fallback): klassischer Login mit Passwort
  const [mode, setMode] = useState('otp_request');
  const [password, setPassword] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpDelivery, setOtpDelivery] = useState(null);
  
  const primaryColor = darkTheme?.accent || settings?.primary_color || '#c9a34e';
  const isDark = themeIsDark(darkTheme);

  // Load Google OAuth config on mount
  useEffect(() => {
    const loadGoogleConfig = async () => {
      try {
        const response = await axios.get(`${API}/portal/auth/google/config`, { headers: { Authorization: portalAuthHeaderValue() } });
        setGoogleConfig(response.data);
      } catch (error) {
        console.log('Google auth not configured');
      }
    };
    loadGoogleConfig();
  }, []);

  // Handle message from Google OAuth popup
  useEffect(() => {
    const handleMessage = async (event) => {
      // Only accept messages from same origin
      if (event.origin !== window.location.origin) return;
      
      // Handle Emergent OAuth callback
      if (event.data?.type === 'oauth_session' && event.data?.session_id) {
        setGoogleLoading(true);
        try {
          const response = await axios.post(`${API}/portal/auth/google/session`, {
            session_id: event.data.session_id
          });
          handleLogin(response.data.token, response.data.customer, response.data.refresh_token);
          
          if (response.data.is_new_user) {
            toast.success(t('portal.login.toast_account_created'));
          } else {
            toast.success(t('portal.login.toast_welcome_back'));
          }
        } catch (error) {
          toast.error(error.response?.data?.detail || t('portal.login.toast_google_failed'));
        } finally {
          setGoogleLoading(false);
        }
      }
      
      // Handle Custom Google OAuth callback (from GoogleOAuthCallback page)
      if (event.data?.type === 'google_oauth_success') {
        setGoogleLoading(true);
        try {
          handleLogin(event.data.token, event.data.customer, event.data.refresh_token);
          
          if (event.data.is_new_user) {
            toast.success(t('portal.login.toast_account_created'));
          } else {
            toast.success(t('portal.login.toast_welcome_back'));
          }
        } finally {
          setGoogleLoading(false);
        }
      }
      
      // Handle Custom Google OAuth error
      if (event.data?.type === 'google_oauth_error') {
        setGoogleLoading(false);
        toast.error(event.data.error || t('portal.login.toast_google_failed'));
      }
    };
    
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleLogin]);

  const handleGoogleLogin = () => {
    if (!googleConfig?.enabled) return;
    
    // Calculate popup position
    const width = 500;
    const height = 600;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;
    
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Custom Google OAuth callback path (must match Google Cloud Console Redirect URI)
    const redirectUrl = window.location.origin + '/portal/google/callback';
    
    let authUrl;
    
    if (googleConfig.provider === 'custom' && googleConfig.client_id) {
      // Use custom Google OAuth credentials
      console.log('Using custom Google OAuth');
      const params = new URLSearchParams({
        client_id: googleConfig.client_id,
        redirect_uri: redirectUrl,
        response_type: 'code',
        scope: 'openid email profile',
        access_type: 'offline',
        prompt: 'consent'
      });
      authUrl = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
    } else {
      // Use Emergent Auth (default)
      console.log('Using Emergent Auth');
      const emergentRedirect = window.location.origin + '/portal';
      authUrl = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(emergentRedirect)}`;
    }
    
    console.log('Google OAuth URL:', authUrl);
    
    // Clear any existing token before starting OAuth
    const existingToken = localStorage.getItem('customerToken');
    
    const popup = window.open(
      authUrl,
      'google-login',
      `width=${width},height=${height},left=${left},top=${top}`
    );
    
    // Start polling to check if token was set in localStorage (backup mechanism)
    // This handles cases where postMessage fails (popup blocked, different origin, etc.)
    setGoogleLoading(true);
    let pollCount = 0;
    const maxPolls = 120; // 2 minutes max
    
    const pollForToken = setInterval(() => {
      pollCount++;
      
      // Check if popup was closed (wrapped in try-catch for COOP policy)
      let popupClosed = false;
      try {
        popupClosed = popup && popup.closed;
      } catch {
        // Cross-Origin-Opener-Policy blocks popup.closed — treat as closed
        popupClosed = true;
      }
      
      if (popupClosed) {
        const newToken = localStorage.getItem('customerToken');
        if (newToken && newToken !== existingToken) {
          // Token was set by the callback page, fetch user data
          console.log('[GoogleLogin] Popup closed, token found in localStorage. Fetching user...');
          clearInterval(pollForToken);
          
          axios.get(`${API}/portal/auth/me`, {
            headers: { Authorization: `Bearer ${newToken}` }
          }).then(response => {
            handleLogin(newToken, response.data, localStorage.getItem('refresh_token'));
            toast.success(t('portal.login.toast_welcome'));
          }).catch(err => {
            console.error('[GoogleLogin] Failed to fetch user after OAuth:', err);
            toast.error(t('portal.login.toast_login_failed'));
          }).finally(() => {
            setGoogleLoading(false);
          });
        } else {
          // Popup closed without token
          console.log('[GoogleLogin] Popup closed, no new token found');
          clearInterval(pollForToken);
          setGoogleLoading(false);
        }
        return;
      }
      
      // Timeout after 2 minutes
      if (pollCount >= maxPolls) {
        console.log('[GoogleLogin] Polling timeout');
        clearInterval(pollForToken);
        setGoogleLoading(false);
      }
    }, 1000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/portal/auth/login`, { email, password });
      
      if (response.data.token && response.data.customer) {
        // Iter 329g Phase 2: refresh_token weiterreichen falls Backend ihn liefert.
        handleLogin(response.data.token, response.data.customer, response.data.refresh_token);
        toast.success(t('portal.login.toast_welcome_back'));
      } else {
        toast.error(t('portal.login.toast_login_failed_invalid'));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('portal.login.toast_login_failed'));
    } finally {
      setLoading(false);
    }
  };

  /** Schritt 1: Code per E-Mail (und optional WhatsApp) anfordern.
   *
   * Backend `/auth/otp/request` erwartet `{email}` oder `{phone}` — kein
   * gemeinsames `identifier`-Feld. Daher müssen wir hier entscheiden, ob
   * der Input eine E-Mail (enthält `@`) oder eine Telefon-Nummer ist.
   */
  const handleRequestOtp = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error(t('portal.login.toast_enter_email'));
      return;
    }
    setLoading(true);
    try {
      const trimmed = (email || '').trim();
      const looksLikeEmail = trimmed.includes('@');
      const payload = looksLikeEmail ? { email: trimmed } : { phone: trimmed };
      const res = await axios.post(`${API}/auth/otp/request`, payload);
      setOtpDelivery(res.data);
      setMode('otp_verify');
      toast.success(t('portal.login.toast_code_sent'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('portal.login.toast_code_send_failed'));
    } finally {
      setLoading(false);
    }
  };

  /** Schritt 2: Code verifizieren → Login-Token. */
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (otpCode.length < 6) {
      toast.error(t('portal.login.toast_enter_full_code'));
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/otp/verify`, {
        identifier: email,
        code: otpCode,
      });
      const data = res.data;
      // Backend liefert TokenResponse: {access_token, refresh_token, token_type, user}.
      // Customer-Profil wird ggf. separat geladen.
      const token = data.access_token || data.token;
      if (token) {
        const customer = data.customer || data.user || { email };
        // Iter 329g: refresh_token weiterreichen damit der globale
        // Axios-Interceptor (siehe AuthContext) auto-refresh kann.
        handleLogin(token, customer, data.refresh_token);
        toast.success(t('portal.login.toast_welcome_back'));
      } else {
        toast.error(t('portal.login.toast_login_failed'));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t('portal.login.toast_code_invalid'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitButtonClick = async (e) => {
    e.preventDefault();
    if (loading) return;
    await handleSubmit(e);
  };

  return (
    <div 
      className="max-w-sm mx-auto rounded-2xl p-8 animate-fade-up"
      style={{
        backgroundColor: darkTheme.cardBg,
        borderColor: darkTheme.border,
        borderWidth: '1px',
      }}
    >
      {/* Logo at top */}
      <div className="flex justify-center mb-6">
        <StrongFitLogo size="lg" showGlow={false} src={settings?.logo_url || '/assets/invictafit-logo.png'} alt={settings?.studio_name || 'Logo'} />
      </div>
      
      <div className="mb-6">
        <h2 className={`text-lg font-semibold flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}
            style={{ fontFamily: "'Outfit', sans-serif" }}>
          <LogIn className="h-4 w-4" style={{ color: primaryColor }} />
          {mode === 'password' ? t('portal.login.title_admin') : t('portal.login.title_login')}
        </h2>
        <p className={`text-sm mt-1 ${isDark ? 'text-white/50' : 'text-white/70'}`}>
          {mode === 'otp_request' && t('portal.login.sub_otp_request')}
          {mode === 'otp_verify' && t('portal.login.sub_otp_verify')}
          {mode === 'password' && t('portal.login.sub_password')}
        </p>
        {/* 06/2026 — Neukunden-Hinweis: Der OTP-Flow ersetzt die separate
            Registrierung. Bestehende UND neue Kunden kommen über denselben
            Weg in die App / das Portal. */}
        {mode === 'otp_request' && (
          <p className={`text-xs mt-2 ${isDark ? 'text-white/40' : 'text-white/60'}`} data-testid="portal-login-new-customer-hint">
            <Trans i18nKey="portal.login.new_customer_hint" components={{ b: <strong /> }} />
          </p>
        )}
      </div>
      
      {/* Google Login wurde komplett aus dem Kundenportal entfernt (User-Wunsch).
          Kunden loggen sich ausschließlich über E-Mail-OTP-Code ein.
          Falls Google Workspace-Auth wieder benötigt wird, hier reaktivieren. */}
      {false && googleConfig?.enabled && mode === 'otp_request' && (
        <>
          <div 
            role="button"
            tabIndex={0}
            data-google-login="true"
            className="google-login-button w-full mb-4 flex items-center justify-center gap-3 h-12 rounded-xl cursor-pointer hover:shadow-md active:scale-[0.97] transition-all"
            style={{
              backgroundColor: '#ffffff',
              border: '1px solid #e5e7eb'
            }}
            onClick={handleGoogleLogin}
            onKeyDown={(e) => e.key === 'Enter' && handleGoogleLogin()}
            data-testid="portal-login-google-button"
          >
            {googleLoading ? (
              <div style={{ height: '20px', width: '20px', border: '2px solid #9ca3af', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
            ) : (
              <GoogleIcon size={20} />
            )}
            <div style={{ color: '#111827', fontWeight: 600, fontSize: '14px' }}>
              {googleLoading ? t('portal.login.google_connecting') : t('portal.login.google_button')}
            </div>
          </div>
          
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className={`w-full border-t ${isDark ? 'border-white/10' : 'border-white/10'}`}></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className={`px-4 ${isDark ? 'bg-[#141310] text-white/40' : 'bg-white/5 text-white/70'}`}>
                {t('portal.login.or')}
              </span>
            </div>
          </div>
        </>
      )}
      
      {/* === OTP REQUEST (Default für Kunden) === */}
      {mode === 'otp_request' && (
        <form onSubmit={handleRequestOtp} className="space-y-4" data-testid="portal-otp-request-form">
          <div>
            <Label htmlFor="otp-email" className={`text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white/60' : ''}`}>{t('portal.login.email_label')}</Label>
            <Input
              id="otp-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder={t('portal.login.email_placeholder')}
              autoFocus
              className={`h-11 rounded-xl ${isDark ? 'bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-yellow-500/50' : ''}`}
              data-testid="portal-otp-email-input"
            />
            <p className={`text-xs mt-1 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
              {t('portal.login.otp_helper')}
            </p>
          </div>
          <Button
            type="submit"
            className="w-full h-11 rounded-xl font-semibold active:scale-[0.98] transition-colors"
            style={{ backgroundColor: primaryColor, color: '#0a0a0a' }}
            disabled={loading || !email}
            data-testid="portal-otp-request-button"
          >
            {loading ? t('portal.login.sending') : t('portal.login.send_code')}
          </Button>
          {/* 06/2026 — Admin-Login-Switch entfernt. Mitarbeiter melden sich
              ebenfalls per OTP-Code an; Passwort-Pfad existiert nur noch
              im Code, ist aber nicht mehr UI-erreichbar. */}
        </form>
      )}

      {/* === OTP VERIFY === */}
      {mode === 'otp_verify' && (
        <form onSubmit={handleVerifyOtp} className="space-y-4" data-testid="portal-otp-verify-form">
          <div className={`rounded-xl p-3 text-xs ${isDark ? 'text-white/70' : 'text-gray-700'}`} style={isDark ? { backgroundColor: darkTheme.bg, border: `1px solid ${darkTheme.border}` } : {}}>
            {t('portal.login.code_sent_to')} <strong>{email}</strong>
            {otpDelivery && (
              <div className={`mt-1 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                {t('portal.login.channels')} {otpDelivery.email ? 'E-Mail' : ''}{otpDelivery.email && otpDelivery.whatsapp ? ' · ' : ''}{otpDelivery.whatsapp ? 'WhatsApp' : ''}
              </div>
            )}
          </div>
          <div>
            <Label htmlFor="otp-code" className={`text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white/60' : ''}`}>{t('portal.login.code_label')}</Label>
            <Input
              id="otp-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="123456"
              autoFocus
              className={`h-12 rounded-xl text-center text-2xl tracking-[0.4em] font-bold ${isDark ? 'bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-yellow-500/50' : ''}`}
              data-testid="portal-otp-code-input"
            />
          </div>
          <Button
            type="submit"
            className="w-full h-11 rounded-xl font-semibold active:scale-[0.98] transition-colors"
            style={{ backgroundColor: primaryColor, color: '#0a0a0a' }}
            disabled={loading || otpCode.length < 6}
            data-testid="portal-otp-verify-button"
          >
            {loading ? t('portal.login.checking') : t('portal.login.login')}
          </Button>
          <button
            type="button"
            className={`w-full text-xs pt-1 transition-colors ${isDark ? 'text-white/40 hover:text-white/70' : 'text-gray-500 hover:text-gray-700'}`}
            onClick={() => { setOtpCode(''); setOtpDelivery(null); setMode('otp_request'); }}
          >
            ← {t('portal.login.use_other_email')}
          </button>
        </form>
      )}

      {/* === PASSWORD LOGIN (Admin / Fallback) === */}
      {mode === 'password' && (
      <form onSubmit={handleSubmit} className="space-y-4" data-testid="portal-login-form">
        <div>
          <Label htmlFor="email" className={`text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white/60' : ''}`}>{t('portal.login.email_label')}</Label>
          <Input 
            id="email" 
            type="email" 
            value={email} 
            onChange={(e) => setEmail(e.target.value)}
            required 
            placeholder={t('portal.login.email_placeholder')}
            className={`h-11 rounded-xl ${isDark ? 'bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-yellow-500/50' : ''}`}
            data-testid="portal-login-email-input"
          />
        </div>
        <div>
          <Label htmlFor="password" className={`text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white/60' : ''}`}>{t('portal.login.password_label')}</Label>
          <Input 
            id="password" 
            type="password" 
            value={password} 
            onChange={(e) => setPassword(e.target.value)}
            required 
            placeholder="••••••••"
            className={`h-11 rounded-xl ${isDark ? 'bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-yellow-500/50' : ''}`}
            data-testid="portal-login-password-input"
          />
        </div>
        <Button 
          type="submit" 
          className="w-full h-11 rounded-xl font-semibold active:scale-[0.98] transition-colors"
          style={{ backgroundColor: primaryColor, color: '#0a0a0a' }}
          disabled={loading}
          onClick={handleSubmitButtonClick}
          data-testid="portal-login-submit-button"
        >
          {loading ? t('portal.login.loading') : t('portal.login.login')}
        </Button>
        {/* Zurück zum Standard-OTP-Login */}
        <button
          type="button"
          className={`w-full text-xs pt-1 transition-colors ${isDark ? 'text-white/40 hover:text-white/70' : 'text-gray-500 hover:text-gray-700'}`}
          onClick={() => setMode('otp_request')}
        >
          ← {t('portal.login.login_via_code')}
        </button>
      </form>
      )}
      
      <div className="mt-4 text-center space-y-2">
        {/* Iter 318 (Feb 2026): "Passwort vergessen?" entfernt — OTP-only,
            keine Passwörter mehr für Kunden. */}
        {/* 06/2026 — "Noch kein Konto? Jetzt registrieren" entfernt.
            Der OTP-Code-Login erledigt die Registrierung implizit
            (E-Mail-Verifikation via Code, Profil-Daten im "Fast geschafft"-
            Screen). Separater Register-Flow nicht mehr nötig. */}
      </div>
    </div>
  );
};


// =============================================
// REGISTER FORM - Multi-Step with Email Verification
// =============================================

// Step Indicator Component (defined outside to avoid re-renders)
const StepIndicator = ({ step, primaryColor }) => (
  <div className="flex items-center justify-center gap-2 mb-6">
    {[1, 2, 3].map((s) => (
      <div key={s} className="flex items-center">
        <div 
          className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
            s === step ? '' : s > step ? 'bg-gray-700 text-white/60' : ''
          }`}
          style={s <= step ? { backgroundColor: primaryColor, color: '#000' } : {}}
        >
          {s < step ? <Check size={16} /> : s}
        </div>
        {s < 3 && (
          <div 
            className="w-8 h-0.5 mx-1"
            style={{ backgroundColor: s < step ? primaryColor : '#374151' }}
          />
        )}
      </div>
    ))}
  </div>
);

export const RegisterForm = ({ onBack, darkTheme }) => {
  const { t } = useTranslation();
  const { handleLogin } = useCustomerAuth();
  const settings = usePortalSettings();
  
  // Step tracking: 1 = Email, 2 = Code, 3 = Details
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationToken, setVerificationToken] = useState('');
  const [countryCode, setCountryCode] = useState('+41'); // Default: Schweiz
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    password: '',
    confirmPassword: '',
    referral_source_id: '',  // Marketing-Quelle
    payment_choice: 'self_payer'  // 08/2026: Zahlungswahl
  });
  
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [referralSources, setReferralSources] = useState([]);
  
  const primaryColor = darkTheme?.accent || settings?.primary_color || '#c9a34e';
  const isDark = themeIsDark(darkTheme);

  // Lade Marketing-Quellen
  useEffect(() => {
    const loadReferralSources = async () => {
      try {
        const response = await axios.get(`${API}/referral-sources/public`);
        setReferralSources(response.data || []);
      } catch (error) {
        console.log('Referral sources not available');
      }
    };
    loadReferralSources();
  }, []);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Step 1: Send verification code
  const handleSendCode = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast.error(t('portal.register.toast_invalid_email'));
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/portal/auth/send-verification-code`, { email });
      toast.success(t('portal.register.toast_code_sent'));
      setStep(2);
      setResendCooldown(60);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('portal.register.toast_send_failed'));
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify code
  const handleVerifyCode = async (e) => {
    e.preventDefault();
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error(t('portal.register.toast_enter_code'));
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/portal/auth/verify-email-code`, {
        email,
        code: verificationCode
      });
      setVerificationToken(response.data.verification_token);
      toast.success(t('portal.register.toast_email_verified'));
      setStep(3);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('portal.register.toast_invalid_code'));
    } finally {
      setLoading(false);
    }
  };

  // Resend code
  const handleResendCode = async () => {
    if (resendCooldown > 0) return;
    
    setLoading(true);
    try {
      await axios.post(`${API}/portal/auth/send-verification-code`, { email });
      toast.success(t('portal.register.toast_new_code_sent'));
      setResendCooldown(60);
      setVerificationCode('');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('portal.register.toast_resend_failed'));
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Complete registration
  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error(t('portal.register.toast_password_mismatch'));
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error(t('portal.register.toast_password_short'));
      return;
    }
    
    if (!formData.phone || formData.phone.trim().length < 5) {
      toast.error(t('portal.register.toast_invalid_phone'));
      return;
    }
    
    if (!acceptedTerms) {
      toast.error(t('portal.register.toast_accept_terms'));
      return;
    }
    
    // Phone already includes country code from PhoneInput
    const fullPhoneNumber = formData.phone;
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/portal/auth/register`, {
        email,
        password: formData.password,
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: fullPhoneNumber,
        accepted_terms: acceptedTerms,
        accepted_terms_at: new Date().toISOString(),
        email_verified: true,
        referral_source_id: formData.referral_source_id || null,  // Marketing-Quelle
        payment_choice: formData.payment_choice,  // 08/2026: Zahlungswahl
        // 08/2026: Partner-Einladung (Link /portal?partner_invite=TOKEN)
        partner_invite_token: new URLSearchParams(window.location.search).get('partner_invite') || null
      }, {
        headers: {
          'X-Verification-Token': verificationToken
        }
      });
      
      handleLogin(response.data.token, response.data.customer, response.data.refresh_token);
      toast.success(t('portal.register.toast_register_success'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('portal.register.toast_register_failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="max-w-sm mx-auto rounded-2xl p-8 animate-fade-up"
      style={{
        backgroundColor: darkTheme.cardBg,
        borderColor: darkTheme.border,
        borderWidth: '1px',
      }}
    >
      {/* Logo at top */}
      <div className="flex justify-center mb-6">
        <StrongFitLogo size="lg" showGlow={false} src={settings?.logo_url || '/assets/invictafit-logo.png'} alt={settings?.studio_name || 'Logo'} />
      </div>
      
      {/* Back button */}
      <button 
        onClick={step === 1 ? onBack : () => setStep(step - 1)} 
        className={`flex items-center gap-1 text-sm mb-4 ${isDark ? 'text-white/60 hover:text-white' : 'text-white/80 hover:text-gray-900'}`}
      >
        <ChevronLeft size={16} />
        {t('portal.register.back')}
      </button>

      {/* Header */}
      <div className="mb-6">
        <h2 className={`text-xl font-bold flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}
            style={{ fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '0.02em' }}>
          <UserPlus className="h-5 w-5" style={{ color: primaryColor }} />
          {t('portal.register.title')}
        </h2>
        <p className={`text-sm mt-1 ${isDark ? 'text-white/50' : 'text-white/70'}`}>
          {step === 1 && t('portal.register.step1_sub')}
          {step === 2 && t('portal.register.step2_sub')}
          {step === 3 && t('portal.register.step3_sub')}
        </p>
      </div>

      <StepIndicator step={step} primaryColor={primaryColor} />

      {/* Step 1: Email */}
      {step === 1 && (
        <form onSubmit={handleSendCode} className="space-y-4">
          <div>
            <Label htmlFor="reg_email" className={`text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white/60' : ''}`}>{t('portal.register.email_label')}</Label>
            <Input 
              id="reg_email" 
              type="email" 
              value={email} 
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('portal.login.email_placeholder')}
              required 
              autoFocus
              className={`h-11 rounded-xl ${isDark ? 'bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-yellow-500/50' : ''}`}
            />
            <p className={`text-xs mt-1 ${isDark ? 'text-white/40' : 'text-white/70'}`}>
              {t('portal.register.email_helper')}
            </p>
          </div>
          
          <Button 
            type="submit" 
            className="w-full h-11 rounded-xl font-semibold active:scale-[0.98] transition-colors" 
            style={{ backgroundColor: primaryColor, color: '#0a0a0a' }}
            disabled={loading}
          >
            {loading ? t('portal.register.sending') : t('portal.register.send_code')}
          </Button>
        </form>
      )}

      {/* Step 2: Code */}
      {step === 2 && (
        <form onSubmit={handleVerifyCode} className="space-y-4">
          <div 
            className="p-3 rounded-lg text-center"
            style={{ 
              backgroundColor: isDark ? 'rgba(234,179,8,0.1)' : 'rgba(234,179,8,0.1)',
              border: `1px solid ${isDark ? 'rgba(234,179,8,0.3)' : 'rgba(234,179,8,0.4)'}`
            }}
          >
            <Mail className="mx-auto mb-2" style={{ color: primaryColor }} size={24} />
            <p className={`text-sm ${isDark ? 'text-white/80' : 'text-white/90'}`}>
              {t('portal.register.code_sent_prefix')} <strong>{email}</strong> {t('portal.register.code_sent_suffix')}
            </p>
          </div>
          
          <div>
            <Label htmlFor="code" className={`text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white/60' : ''}`}>{t('portal.register.code_label')}</Label>
            <Input 
              id="code" 
              type="text" 
              value={verificationCode} 
              onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              maxLength={6}
              required 
              autoFocus
              className={`text-center text-2xl tracking-widest font-mono ${isDark ? 'bg-white/5 border-white/10 text-white placeholder:text-white/40' : ''}`}
            />
          </div>
          
          <Button 
            type="submit" 
            className="w-full h-11 rounded-xl font-semibold active:scale-[0.98] transition-colors" 
            style={{ backgroundColor: primaryColor, color: '#0a0a0a' }}
            disabled={loading || verificationCode.length !== 6}
          >
            {loading ? t('portal.register.checking') : t('portal.register.confirm_code')}
          </Button>
          
          <div className="text-center">
            <button
              type="button"
              onClick={handleResendCode}
              disabled={resendCooldown > 0 || loading}
              className={`text-sm ${resendCooldown > 0 ? 'opacity-50' : 'hover:underline'}`}
              style={{ color: primaryColor }}
            >
              {resendCooldown > 0 ? t('portal.register.resend_in', { seconds: resendCooldown }) : t('portal.register.resend')}
            </button>
          </div>
        </form>
      )}

      {/* Step 3: Details */}
      {step === 3 && (
        <form onSubmit={handleRegister} className="space-y-4" autoComplete="off">
          <div 
            className="p-3 rounded-lg flex items-center gap-2"
            style={{ 
              backgroundColor: isDark ? 'rgba(234,179,8,0.1)' : 'rgba(234,179,8,0.1)',
              border: `1px solid ${isDark ? 'rgba(234,179,8,0.3)' : 'rgba(234,179,8,0.4)'}`
            }}
          >
            <Check style={{ color: primaryColor }} size={20} />
            <span className={`text-sm ${isDark ? 'text-white/80' : 'text-white/90'}`}>
              {t('portal.register.email_verified')} {email}
            </span>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="first_name" className={`text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white/60' : ''}`}>{t('portal.register.first_name')}</Label>
              <Input 
                id="first_name"
                name="first_name"
                autoComplete="given-name"
                value={formData.first_name} 
                onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                required 
                autoFocus
                className={`h-11 rounded-xl ${isDark ? 'bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-yellow-500/50' : ''}`}
              />
            </div>
            <div>
              <Label htmlFor="last_name" className={`text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white/60' : ''}`}>{t('portal.register.last_name')}</Label>
              <Input 
                id="last_name"
                name="last_name"
                autoComplete="family-name"
                value={formData.last_name} 
                onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                required 
                className={`h-11 rounded-xl ${isDark ? 'bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-yellow-500/50' : ''}`}
              />
            </div>
          </div>
          
          <div>
            <Label htmlFor="phone" className={`text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white/60' : ''}`}>{t('portal.register.phone')}</Label>
            <PhoneInput
              id="phone"
              data-testid="register-phone-input"
              value={formData.phone}
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
              isDark={isDark}
            />
          </div>
          
          <div>
            <Label htmlFor="password" className={`text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white/60' : ''}`}>{t('portal.register.password')}</Label>
            <Input 
              id="password"
              name="new-password"
              type="password"
              autoComplete="new-password"
              value={formData.password} 
              onChange={(e) => setFormData({...formData, password: e.target.value})}
              required 
              minLength={6}
              className={`h-11 rounded-xl ${isDark ? 'bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-yellow-500/50' : ''}`}
            />
          </div>
          
          <div>
            <Label htmlFor="confirmPassword" className={`text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white/60' : ''}`}>{t('portal.register.confirm_password')}</Label>
            <Input 
              id="confirmPassword"
              name="confirm-password"
              type="password"
              autoComplete="new-password"
              value={formData.confirmPassword} 
              onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
              required 
              minLength={6}
              className={`h-11 rounded-xl ${isDark ? 'bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-yellow-500/50' : ''}`}
            />
          </div>
          
          {/* 08/2026: Zahlungswahl */}
          <div>
            <Label className={`text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white/60' : ''}`}>{t('portal.register.payment_question')}</Label>
            <div className="grid grid-cols-1 gap-2 mt-2">
              {[
                { value: 'self_payer', title: t('portal.register.payment_self_title'), desc: t('portal.register.payment_self_desc') },
                { value: 'auto_charge', title: t('portal.register.payment_auto_title'), desc: t('portal.register.payment_auto_desc') },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  data-testid={`register-payment-${opt.value}`}
                  onClick={() => setFormData({ ...formData, payment_choice: opt.value })}
                  className={`text-left p-3 rounded-xl border transition-colors ${
                    formData.payment_choice === opt.value
                      ? 'border-yellow-500 ring-1 ring-yellow-500/50 ' + (isDark ? 'bg-yellow-500/10' : 'bg-yellow-50')
                      : isDark ? 'bg-white/5 border-white/10 hover:border-white/25' : 'bg-white/5 border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <div className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{opt.title}</div>
                  <div className={`text-xs mt-0.5 ${isDark ? 'text-white/50' : 'text-gray-600'}`}>{opt.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Wie haben Sie von uns erfahren? (Optional) */}
          {referralSources.length > 0 && (
            <div>
              <Label className={`text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white/60' : ''}`}>{t('portal.register.referral_question')}</Label>
              <select
                value={formData.referral_source_id}
                onChange={(e) => setFormData({...formData, referral_source_id: e.target.value})}
                className={`w-full h-11 px-3 rounded-xl border text-sm ${
                  isDark 
                    ? 'bg-white/5 border-white/10 text-white' 
                    : 'bg-white/5 border-gray-300 text-gray-900'
                }`}
              >
                <option value="">{t('portal.register.referral_placeholder')}</option>
                {referralSources.map((source) => (
                  <option key={source.id} value={source.id}>
                    {source.icon} {source.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          
          {/* AGB & Datenschutz Checkbox */}
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="acceptTerms"
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-gray-300 text-yellow-500 focus:ring-yellow-500"
              style={{ accentColor: primaryColor }}
              required
            />
            <label 
              htmlFor="acceptTerms" 
              className={`text-sm ${isDark ? 'text-white/70' : 'text-white/80'}`}
            >
              {t('portal.register.accept_prefix')}{' '}
              <a 
                href="/agb" 
                target="_blank" 
                rel="noopener noreferrer"
                className="underline hover:no-underline"
                style={{ color: primaryColor }}
              >
                {t('portal.register.agb')}
              </a>
              {' '}{t('portal.register.and')}{' '}
              <a 
                href="/datenschutz" 
                target="_blank" 
                rel="noopener noreferrer"
                className="underline hover:no-underline"
                style={{ color: primaryColor }}
              >
                {t('portal.register.privacy')}
              </a>
              {' '}*
            </label>
          </div>
          
          <Button 
            type="submit" 
            className="w-full h-11 rounded-xl font-semibold active:scale-[0.98] transition-colors" 
            style={{ backgroundColor: primaryColor, color: '#0a0a0a' }}
            disabled={loading || !acceptedTerms}
          >
            {loading ? t('portal.register.registering') : t('portal.register.finish')}
          </Button>
        </form>
      )}
    </div>
  );
};
